import { Platform } from 'react-native';
import type {
  CumulativeReportData,
  CumulativeReportPeriod,
} from '@exhale/contracts';
import { currentEnvironment, isDetoxTestControlsEnabled, shouldMockNativeSharing } from '../config/environments';
import { api } from './api';

export interface CumulativeReportDownloadResult {
  fileName: string;
  uri: string;
}

const monthPattern = /^(\d{4})-(\d{2})$/;
const datePattern = /^(\d{4})-(\d{2})-(\d{2})$/;

export const cumulativeReportEffectiveFromForDate = (value: string): string => {
  const match = datePattern.exec(value.trim());
  if (!match) {
    throw new Error('Choose a reporting start date in YYYY-MM-DD format.');
  }

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, monthIndex, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== monthIndex ||
    date.getUTCDate() !== day
  ) {
    throw new Error('Choose a valid reporting start date.');
  }

  return date.toISOString();
};

export const cumulativeReportPeriodForMonth = (month: string): CumulativeReportPeriod => {
  const match = monthPattern.exec(month.trim());
  if (!match) {
    throw new Error('Choose a month in YYYY-MM format.');
  }

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  if (monthIndex < 0 || monthIndex > 11) {
    throw new Error('Choose a valid calendar month.');
  }

  return {
    startInclusive: new Date(Date.UTC(year, monthIndex, 1)).toISOString(),
    endExclusive: new Date(Date.UTC(year, monthIndex + 1, 1)).toISOString(),
  };
};

export const shiftCumulativeReportMonth = (month: string, delta: number): string => {
  const period = cumulativeReportPeriodForMonth(month);
  const date = new Date(period.startInclusive);
  date.setUTCMonth(date.getUTCMonth() + delta);
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
};

const reportPath = (
  templateId: number,
  action: 'preview' | 'download',
  period: CumulativeReportPeriod
) => {
  const params = new URLSearchParams({
    start: String(period.startInclusive),
    end: String(period.endExclusive),
  });
  return `/admin/templates/${templateId}/cumulative-report/${action}?${params.toString()}`;
};

const safeFilePart = (value: string) => value
  .toLocaleLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '') || 'template';

const responseError = async (response: Response, fallback: string): Promise<Error> => {
  const text = await response.text().catch(() => '');
  try {
    const parsed = text ? JSON.parse(text) : {};
    return new Error(parsed.message || fallback);
  } catch {
    return new Error(text || fallback);
  }
};

export const adminCumulativeReportService = {
  preview(
    templateId: number,
    period: CumulativeReportPeriod
  ): Promise<CumulativeReportData> {
    return api.get<CumulativeReportData>(reportPath(templateId, 'preview', period));
  },

  async downloadAndShare(
    templateId: number,
    templateName: string,
    month: string,
    period: CumulativeReportPeriod
  ): Promise<CumulativeReportDownloadResult> {
    const endpoint = reportPath(templateId, 'download', period);
    const token = await api.getToken();
    const url = `${currentEnvironment.apiUrl}${endpoint}`;
    const fileName = `${safeFilePart(templateName)}-cumulative-${month}.pdf`;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    if (Platform.OS === 'web') {
      const response = await fetch(url, { method: 'GET', headers });
      if (!response.ok) {
        throw await responseError(response, 'Failed to download cumulative report.');
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = fileName;
      anchor.rel = 'noopener noreferrer';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(objectUrl);
      return { fileName, uri: objectUrl };
    }

    const FileSystem = require('expo-file-system/legacy');
    const Sharing = require('expo-sharing');
    const directory = FileSystem.cacheDirectory || FileSystem.documentDirectory;
    if (!directory) {
      throw new Error('No writable directory is available for the report.');
    }

    const result = await FileSystem.downloadAsync(url, `${directory}${fileName}`, { headers });
    if (typeof result.status === 'number' && (result.status < 200 || result.status >= 300)) {
      throw new Error(`Cumulative report download failed with status ${result.status}.`);
    }

    // Detox still exercises the authenticated download endpoint, but avoids a
    // nondeterministic native share sheet once the PDF is on disk.
    if (!isDetoxTestControlsEnabled && !shouldMockNativeSharing) {
      const available = await Sharing.isAvailableAsync();
      if (available) {
        await Sharing.shareAsync(result.uri, {
          mimeType: 'application/pdf',
          dialogTitle: 'Share cumulative report',
          UTI: 'com.adobe.pdf',
        });
      }
    }

    return { fileName, uri: result.uri };
  },
};

export default adminCumulativeReportService;
